import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Input,
  Typography,
  Box,
  Grid,
} from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import "./styles.css";

interface EditMetaAcronymModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialMetadata?: {
    organizations: string[];
    industries: string[];
    technologies: string[];
    document_types?: string[];
    regions: string[];
  };
  initialAcronyms?: {
    organizations: string[];
    industries: string[];
    technologies: string[];
    document_types?: string[];
    regions: string[];
  };
  onSubmit: (data: { metadata: any; acronyms: any }) => void;
  loading?: boolean;
  excludeDocumentTypes?: boolean;
}

const defaultFields = ["organizations", "industries", "technologies", "document_types","regions"];

const EditMetaAcronymModal: React.FC<EditMetaAcronymModalProps> = ({
  open,
  setOpen,
  initialMetadata,
  initialAcronyms,
  onSubmit,
  loading = false,
  excludeDocumentTypes = false,
}) => {
  const fieldsToShow = excludeDocumentTypes 
    ? defaultFields.filter(field => field !== "document_types")
    : defaultFields;
  
  const getInitialState = () => {
    const baseState: any = {
      organizations: [""],
      industries: [""],
      technologies: [""],
      regions: [""],
    };
    if (!excludeDocumentTypes) {
      baseState.document_types = [""];
    }
    return baseState;
  };

  const [metadata, setMetadata] = useState<any>(getInitialState());
  const [acronyms, setAcronyms] = useState<any>(getInitialState());

  useEffect(() => {
    // Helper to ensure at least one empty string per field
    const ensureAtLeastOne = (arr: any[]) => (Array.isArray(arr) && arr.length > 0 ? arr : []);
    if (initialMetadata) {
      const metadataState: any = {
        organizations: ensureAtLeastOne(initialMetadata.organizations),
        industries: ensureAtLeastOne(initialMetadata.industries),
        technologies: ensureAtLeastOne(initialMetadata.technologies),
        regions: ensureAtLeastOne(initialMetadata.regions),
      };
      if (!excludeDocumentTypes) {
        metadataState.document_types = ensureAtLeastOne(initialMetadata.document_types || []);
      }
      setMetadata(metadataState);
    }
    if (initialAcronyms) {
      const acronymsState: any = {
        organizations: ensureAtLeastOne(initialAcronyms.organizations),
        industries: ensureAtLeastOne(initialAcronyms.industries),
        technologies: ensureAtLeastOne(initialAcronyms.technologies),
        regions: ensureAtLeastOne(initialAcronyms.regions),
      };
      if (!excludeDocumentTypes) {
        acronymsState.document_types = ensureAtLeastOne(initialAcronyms.document_types || []);
      }
      setAcronyms(acronymsState);
    }
  }, [initialMetadata, initialAcronyms, open, excludeDocumentTypes]);

  const handleMetaChange = (field: string, idx: number, value: string) => {
    setMetadata((prev: any) => ({
      ...prev,
      [field]: prev[field].map((v: string, i: number) => (i === idx ? value : v)),
    }));
  };
  const handleAcronymChange = (field: string, idx: number, value: string) => {
    setAcronyms((prev: any) => ({
      ...prev,
      [field]: prev[field].map((v: string, i: number) => (i === idx ? value : v)),
    }));
  };
  const handleAddItem = (field: string) => {
    setMetadata((prev: any) => ({ ...prev, [field]: [...prev[field], ""] }));
    setAcronyms((prev: any) => ({ ...prev, [field]: [...prev[field], ""] }));
  };
  const handleRemoveItem = (field: string, idx: number) => {
    setMetadata((prev: any) => ({ ...prev, [field]: prev[field].filter((_: string, i: number) => i !== idx) }));
    setAcronyms((prev: any) => ({ ...prev, [field]: prev[field].filter((_: string, i: number) => i !== idx) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ metadata, acronyms });
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalDialog sx={{ width: 500 }}>
        <DialogTitle>Edit Metadata & Acronyms</DialogTitle>
        <IconButton sx={{ position: "absolute", top: 8, right: 8 }} onClick={() => setOpen(false)}>
          <CloseIcon />
        </IconButton>
        <Divider />
        
        <form onSubmit={handleSubmit} >
          <DialogContent  className="edit-meta-acronym-modal">
            {fieldsToShow.map((field) => (
              <Box key={field} sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1 }}>{field.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</Typography>
                {metadata[field].map((val: string, idx: number) => (
                  <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <Input
                      value={val}
                      placeholder={`Enter ${field.replace("_", " ")}`}
                      onChange={e => handleMetaChange(field, idx, e.target.value)}
                      sx={{ flex: 2 }}
                    />
                    <Input
                      value={acronyms[field][idx]}
                      placeholder="Acronym"
                      onChange={e => handleAcronymChange(field, idx, e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <IconButton onClick={() => handleRemoveItem(field, idx)} >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="sm" variant="soft" onClick={() => handleAddItem(field)} type="button">
                  Add {field}
                </Button>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="solid" sx={{ backgroundColor: "#074c90" }} loading={loading}>
              Update
            </Button>
            <Button variant="outlined" color="neutral" onClick={() => setOpen(false)} type="button">
              Cancel
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default EditMetaAcronymModal; 